import { BODY_TOOLTIP, LOGGER_LOG_TYPE } from 'Config';
import Button from 'common/components/button/Button';
import InputError from 'common/components/inputError/InputError';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import TextInput, { TextInputController } from 'common/components/textInput/TextInput';
import CheckInput from 'common/components/checkInput/CheckInput';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';
import { Label } from 'common/components/texts/Texts';
import Loading from 'common/services/Loading';
import Toast from 'common/services/Toast';
import { useState, useEffect, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaTrashAlt } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { CatalogParameterDto } from 'api/catalogParameters/models/CatalogParameterDto';
import CatalogParametersService from 'api/catalogParameters/CatalogParametersService';
import { Form } from 'common/components/form/Form';
import SelectInput from 'common/components/selectInput/SelectInput';
import { ParameterSectionParameterType } from 'common/components/parametersSections/models';
import { UnitDto } from 'api/units/models/UnitDto';
import UnitsService from 'api/units/UnitsService';

function CatalogParameterScreen (): JSX.Element | null {
    const { id, type } = useParams<{ id: string; type: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const form = useForm<CatalogParameterDto>({ shouldUnregister: false });
    const errors = form.formState.errors;
    const isDetails = type === 'details';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [units, setUnits] = useState<UnitDto[]>([]);
    const [allCatalogParams, setAllCatalogParams] = useState<CatalogParameterDto[]>([]);
    const saveAndNewRef = useRef(false);

    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['CATALOG_PARAMETERS_WRITE']);
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['CATALOG_PARAMETERS_READ']);

    const url = '/library/catalog-parameters';
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');

    const parameterTypeOptions = Object.keys(ParameterSectionParameterType)
        .filter(key => key !== ParameterSectionParameterType.NUMBER_MIN_MAX)
        .map(key => ({
            label: t('common.parameters_sections.parameters_types.' + key),
            value: key,
        })).sort((a, b) => a.label.localeCompare(b.label));

    const unitOptions = units.map(u => ({
        label: `${u.name} (${u.symbol})`,
        value: u.id,
    }));

    useEffect(() => {
        void getData();
    }, [id, type]);

    const getData = async () => {
        try {
            let result: Partial<CatalogParameterDto> = { id, name: '' };
            Loading.show();

            const unitsResult = await UnitsService.getCatalog();
            setUnits(unitsResult);

            const catalogResult = await CatalogParametersService.getCatalog();
            setAllCatalogParams(catalogResult);

            if (id) {
                result = await CatalogParametersService.getById(id);
            }

            if (type === 'duplicate') {
                result.name = (result.name ?? '') + ' - ' + t('common.copy');
                result.id = undefined;
                result.rowVersion = undefined;
            }

            form.reset(result);
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get catalog parameter information', error);
            Toast.error(t('messages.error_load_info'));
        }
    };

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`${url}/${typeUrl}/${id}`);
        } else {
            navigate(url);
        }
    };

    const onSubmit = async (model: CatalogParameterDto) => {
        try {
            Loading.show();

            if (model && model.id) {
                await CatalogParametersService.update(model);
                void getData();
            } else if (model) {
                await CatalogParametersService.create(model);
                if (saveAndNewRef.current) {
                    saveAndNewRef.current = false;
                    const empty: Partial<CatalogParameterDto> = { name: '' };
                    form.reset(empty);
                    navigateTo('create');
                } else {
                    navigateTo();
                }
            }
            Loading.hide();
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the catalog parameter with id: ${id}`, error);
            const msg = error?.response?.data;
            if (typeof msg === 'string' && msg.includes('CATALOG_PARAMETER_NAME_ALREADY_EXISTS')) {
                Toast.error(t('catalog_parameters.errors.name_already_exists'));
            } else {
                Toast.error(t('messages.record_save_error'));
            }
            Loading.hide();
        }
    };

    const onInvalid = () => {
        Toast.warning(t('messages.required_fields_empty'));
    };

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false);
            return;
        }

        Loading.show();
        try {
            await CatalogParametersService.remove(form.getValues());
            Toast.success(t('messages.record_delete_success'));
            navigateTo();
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete catalog parameter', error);
            Toast.error(t('messages.record_delete_error'));
        } finally {
            Loading.hide();
            setShowConfirmDeleteModal(false);
        }
    };

    const getFormulaParams = (): ExpressionParameter[] => {
        const currentId = form.getValues('id');
        const numericTypes = ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'];
        const filtered = allCatalogParams
            .filter(cp => cp.id !== currentId && cp.parameterType && numericTypes.includes(cp.parameterType));

        return filtered.map(cp => ({
            id: ExpressionSystem.formatParameterId([cp.id]),
            name: ExpressionSystem.formatParameterName([cp.name]),
            value: 0,
        }));
    };

    const entityName = form.watch('name');
    const titlePrefix = type === 'create' || type === 'duplicate'
        ? t('common.new_title.catalog_parameter')
        : type === 'edit'
            ? t('common.edit_title.catalog_parameter')
            : t('common.details_title.catalog_parameter');
    const entityLabel = type !== 'create' && type !== 'duplicate' && entityName ? entityName : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('catalog_parameters.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('catalog_parameters.title'), url },
                    { name: title, url: currentUrl },
                ]}
            />
            <Form handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate>
                <PageHeader title={titlePrefix} subTitle={entityLabel} onGoBack={() => navigateTo()} informationText={t('common.go_back')}>
                    {canWrite && !isDetails && <Button type='submit'>{t('common.save')}</Button>}
                    {canWrite && !isDetails && (type === 'create' || type === 'duplicate') && (
                        <Button type='submit' variant='secondary' className='ms-2' onClick={() => { saveAndNewRef.current = true; }}>
                            {t('catalog_parameters.save_and_new')}
                        </Button>
                    )}
                    {canWrite && isDetails && <Button fw href={`${url}/edit/${id}`}>{t('common.edit')}</Button>}
                    {canWrite && isDetails && <Button fw onClick={() => { navigateTo('duplicate', id); }}>{t('common.duplicate')}</Button>}
                    {canWrite && isDetails && (
                        <Button variant='danger' onClick={() => setShowConfirmDeleteModal(true)}>
                            <FaTrashAlt />
                        </Button>
                    )}
                </PageHeader>
                <PageContainer>
                    <Row>
                        <Col xs={6}>
                            <Label space>{t('catalog_parameters.list.name')}{!isDetails ? '*' : ''}</Label>
                            <TextInputController
                                name='name' control={form.control}
                                placeholder={t('catalog_parameters.list.name')} disabled={isDetails}
                                rules={{ required: true, maxLength: 500 }}
                                hasError={Boolean(errors.name)} />
                            <InputError error={errors.name} maxLength={500} />
                        </Col>
                        <Col xs={6}>
                            <Label space>{t('catalog_parameters.list.parameter_type')}{!isDetails ? '*' : ''}</Label>
                            <SelectInput
                                options={parameterTypeOptions}
                                value={form.watch('parameterType') ?? ''}
                                onChange={(v) => form.setValue('parameterType', v, { shouldDirty: true })}
                                disabled={isDetails}
                                hasError={Boolean(errors.parameterType)}
                            />
                        </Col>
                    </Row>
                    <Row className='mt-3'>
                        <Col xs={6}>
                            <Label space>{t('catalog_parameters.list.unit')}</Label>
                            <SelectInput
                                options={unitOptions}
                                value={form.watch('unitId') ?? ''}
                                onChange={(v) => form.setValue('unitId', v || null, { shouldDirty: true })}
                                disabled={isDetails}
                            />
                        </Col>
                        <Col xs={6}>
                            <Label space>{t('catalog_parameters.list.formula')}</Label>
                            <TextInput
                                value={ExpressionSystem.formatExpressionForDisplay(
                                    form.watch('formulaExpression') ?? '',
                                    getFormulaParams()
                                )}
                                onChange={() => {}}
                                placeholder={t('common.parameters_sections.formula_placeholder')}
                                readOnly={true}
                                style={{ cursor: 'default' }}
                                expressionButton={{
                                    value: form.watch('formulaExpression') ?? '',
                                    title: form.watch('name') ?? t('catalog_parameters.list.formula'),
                                    loadParameters: getFormulaParams,
                                    onChange: (expr: string | null) => form.setValue('formulaExpression', expr, { shouldDirty: true }),
                                    disabled: isDetails,
                                }}
                            />
                        </Col>
                    </Row>
                    {['NUMBER', 'QUANTITY', 'PRICE', 'WEIGHT', 'NUMBER_MIN_MAX'].includes(form.watch('parameterType') ?? '') && (
                        <Row className='mt-3'>
                            <Col xs={6}>
                                <CheckInput
                                    checked={form.watch('groupInSummary') ?? false}
                                    disabled={isDetails}
                                    onChange={(e) => form.setValue('groupInSummary', e.target.checked, { shouldDirty: true })}
                                >
                                    {t('catalog_parameters.group_in_summary')}
                                    <InfoIcon
                                        data-tooltip-id={BODY_TOOLTIP}
                                        data-tooltip-content={t('catalog_parameters.group_in_summary_tooltip')}
                                        style={{ marginLeft: 6, cursor: 'pointer' }}
                                    />
                                </CheckInput>
                            </Col>
                            <Col xs={6}>
                                <CheckInput
                                    checked={form.watch('exportInMaterialsList') ?? false}
                                    disabled={isDetails}
                                    onChange={(e) => form.setValue('exportInMaterialsList', e.target.checked, { shouldDirty: true })}
                                >
                                    {t('catalog_parameters.export_in_materials_list')}
                                    <InfoIcon
                                        data-tooltip-id={BODY_TOOLTIP}
                                        data-tooltip-content={t('catalog_parameters.export_in_materials_list_tooltip')}
                                        style={{ marginLeft: 6, cursor: 'pointer' }}
                                    />
                                </CheckInput>
                            </Col>
                        </Row>
                    )}
                </PageContainer>
            </Form>
            <ConfirmDeleteModal
                itemName={form.getValues('name')}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    );
}

export default CatalogParameterScreen;
